import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Save, 
  Clock,
  AlertCircle,
  CheckCircle,
  X,
  Ban
} from 'lucide-react';
import { 
  weeklySlotApiService, 
  WeeklySlot, 
  APPOINTMENT_TYPES 
} from '../services/weeklySlotApi';

const DateSlotManager: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [slots, setSlots] = useState<WeeklySlot[]>([]);
  const [allSlots, setAllSlots] = useState<WeeklySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [unavailableDates, setUnavailableDates] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadAllSlots();
  }, []);

  useEffect(() => {
    filterSlotsForDate(selectedDate);
  }, [selectedDate, allSlots]);

  const loadAllSlots = async () => {
    try {
      setLoading(true);
      const data = await weeklySlotApiService.getWeeklySlots();
      setAllSlots(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterSlotsForDate = (date: Date) => {
    const weekday = date.getDay();
    const filteredSlots = allSlots.filter(slot => slot.weekday === weekday);
    setSlots(filteredSlots);
  };

  const handleSaveSlots = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const weekday = selectedDate.getDay();
      const otherDaySlots = allSlots.filter(slot => slot.weekday !== weekday);
      const allUpdatedSlots = [...otherDaySlots, ...slots];
      
      await weeklySlotApiService.bulkUpdateWeeklySlots(allUpdatedSlots);
      setSuccess('Créneaux sauvegardés avec succès !');
      await loadAllSlots();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSlot = () => {
    const newSlot: WeeklySlot = {
      weekday: selectedDate.getDay(),
      start_time: '09:00',
      end_time: '10:00',
      appointment_type: 'both',
      max_bookings: 1,
      is_active: true,
      notes: ''
    };
    setSlots([...slots, newSlot]);
  };

  const handleUpdateSlot = (index: number, field: keyof WeeklySlot, value: any) => {
    const updatedSlots = [...slots];
    updatedSlots[index] = { ...updatedSlots[index], [field]: value };
    setSlots(updatedSlots);
  };

  const handleDeleteSlot = (index: number) => {
    setSlots(slots.filter((_, i) => i !== index));
  };

  const toggleUnavailableDate = () => {
    const dateStr = selectedDate.toISOString().split('T')[0];
    const newUnavailableDates = new Set(unavailableDates);
    
    if (newUnavailableDates.has(dateStr)) {
      newUnavailableDates.delete(dateStr);
    } else {
      newUnavailableDates.add(dateStr);
    }
    
    setUnavailableDates(newUnavailableDates);
  };

  const isDateUnavailable = (date: Date): boolean => {
    const dateStr = date.toISOString().split('T')[0];
    return unavailableDates.has(dateStr);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.toDateString() === date2.toDateString();
  };

  const changeMonth = (offset: number) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + offset);
    setCurrentMonth(newMonth);
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  const isSelectedDateUnavailable = unavailableDates.has(selectedDateStr);

  return (
    <div className="space-y-6">
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800">{success}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendrier */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ←
              </button>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                →
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              const isUnavailable = isDateUnavailable(day);

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : isUnavailable
                      ? 'bg-red-100 text-red-800 line-through'
                      : isToday
                      ? 'bg-blue-100 text-blue-900'
                      : 'hover:bg-gray-100 text-gray-900'
                  }`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Gestion des créneaux */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 capitalize">
                {formatDate(selectedDate)}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {slots.length} créneau{slots.length > 1 ? 'x' : ''}
              </p>
            </div>
            <button
              onClick={toggleUnavailableDate}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                isSelectedDateUnavailable
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              <Ban className="w-4 h-4" />
              <span className="text-sm">
                {isSelectedDateUnavailable ? 'Disponible' : 'Indisponible'}
              </span>
            </button>
          </div>

          <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
            {slots.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Aucun créneau pour cette date</p>
              </div>
            ) : (
              slots.map((slot, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Début
                      </label>
                      <input
                        type="time"
                        value={slot.start_time}
                        onChange={(e) => handleUpdateSlot(index, 'start_time', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Fin
                      </label>
                      <input
                        type="time"
                        value={slot.end_time}
                        onChange={(e) => handleUpdateSlot(index, 'end_time', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Type de rendez-vous
                    </label>
                    <select
                      value={slot.appointment_type}
                      onChange={(e) => handleUpdateSlot(index, 'appointment_type', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      {APPOINTMENT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Max réservations
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={slot.max_bookings}
                        onChange={(e) => handleUpdateSlot(index, 'max_bookings', parseInt(e.target.value))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={slot.is_active}
                          onChange={(e) => handleUpdateSlot(index, 'is_active', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-xs text-gray-700">Actif</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => handleDeleteSlot(index)}
                      className="flex items-center space-x-1 text-red-600 hover:text-red-800 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleAddSlot}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter un créneau</span>
            </button>
            <button
              onClick={handleSaveSlots}
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateSlotManager;
